import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Grid, Button, Tabs, Tab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { toast } from '../../components/toast';
import { getUserGroups } from '../../utils';
import { useLeaveOperations } from '../../hooks/useLeaveOperations';
import { leaveValidationSchema, leaveSettingsValidationSchema } from '../../utils/leaveValidation';
import {
  getInitialLeaveFormData,
  getInitialLeaveSettingsFormData,
  formatLeaveDataForSubmission,
  formatLeaveSettingsDataForSubmission,
  getLeaveFormDataFromRecord,
  getLeaveSettingsFormDataFromRecord,
} from '../../utils/leaveUtils';
import {
  LeaveStatsCards,
  LeaveFilters,
  LeaveSettingsFilters,
  LeaveTable,
  LeaveSettingsTable,
  LeaveForm,
  LeaveSettingsForm,
} from '../../components/leave';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

const allowedGroups = ['admin', 'hr'];

export default function LeaveManagementPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('requests');

  const {
    isLoading,
    leaves,
    stats,
    totalCount,
    filterOptions,
    allEmployees,
    loadFilterOptions,
    loadLeaves,
    createLeaveRecord,
    updateLeaveRecord,
    deleteLeaveRecord,
    isSettingsLoading,
    leaveSettings,
    settingsTotalCount,
    loadLeaveSettings,
    createLeaveSettingsRecord,
    updateLeaveSettingsRecord,
    deleteLeaveSettingsRecord,
  } = useLeaveOperations();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [editingLeave, setEditingLeave] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [employeeFilter, setEmployeeFilter] = useState('All');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('All');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    preset: 'All',
  });
  const [formData, setFormData] = useState(getInitialLeaveFormData());
  const [formErrors, setFormErrors] = useState({});
  const [settingsPage, setSettingsPage] = useState(0);
  const [settingsRowsPerPage, setSettingsRowsPerPage] = useState(10);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [settingsDialogMode, setSettingsDialogMode] = useState('add');
  const [editingSettings, setEditingSettings] = useState(null);
  const [openSettingsDeleteDialog, setOpenSettingsDeleteDialog] = useState(false);
  const [settingsToDelete, setSettingsToDelete] = useState(null);
  const [settingsSearchTerm, setSettingsSearchTerm] = useState('');
  const [settingsEmployeeFilter, setSettingsEmployeeFilter] = useState('All');
  const [settingsFormData, setSettingsFormData] = useState(getInitialLeaveSettingsFormData());
  const [settingsFormErrors, setSettingsFormErrors] = useState({});

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleLoadLeaves = useCallback(async () => {
    await loadLeaves(
      page,
      rowsPerPage,
      searchTerm,
      statusFilter,
      employeeFilter,
      leaveTypeFilter,
      dateRange.startDate,
      dateRange.endDate
    );
  }, [
    loadLeaves,
    page,
    rowsPerPage,
    searchTerm,
    statusFilter,
    employeeFilter,
    leaveTypeFilter,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const handleLoadLeaveSettings = useCallback(async () => {
    await loadLeaveSettings(settingsPage, settingsRowsPerPage, settingsSearchTerm, settingsEmployeeFilter);
  }, [loadLeaveSettings, settingsPage, settingsRowsPerPage, settingsSearchTerm, settingsEmployeeFilter]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    if (activeTab === 'requests') {
      handleLoadLeaves();
    }
  }, [handleLoadLeaves, activeTab]);

  useEffect(() => {
    if (activeTab === 'settings') {
      handleLoadLeaveSettings();
    }
  }, [handleLoadLeaveSettings, activeTab]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (mode, leave = null) => {
    setDialogMode(mode);
    setEditingLeave(leave);
    setOpenDialog(true);

    if (mode !== 'add' && leave) {
      setFormData(getLeaveFormDataFromRecord(leave));
    } else {
      setFormData(getInitialLeaveFormData());
    }
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLeave(null);
    setFormData(getInitialLeaveFormData());
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = async () => {
    try {
      await leaveValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fix the form errors and try again.');
      return;
    }

    const submitData = formatLeaveDataForSubmission(formData);
    const success =
      dialogMode === 'edit'
        ? await updateLeaveRecord(editingLeave.id, submitData)
        : await createLeaveRecord(submitData);

    if (success) {
      handleCloseDialog();
      handleLoadLeaves();
    }
  };

  const handleDeleteLeave = (leave) => {
    setLeaveToDelete(leave);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    const success = await deleteLeaveRecord(leaveToDelete.id);
    if (success) {
      setOpenDeleteDialog(false);
      setLeaveToDelete(null);
      handleLoadLeaves();
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setLeaveToDelete(null);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'employee':
        setEmployeeFilter(value);
        break;
      case 'leaveType':
        setLeaveTypeFilter(value);
        break;
      default:
        break;
    }
    setPage(0);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setEmployeeFilter('All');
    setLeaveTypeFilter('All');
    setDateRange({
      startDate: null,
      endDate: null,
      preset: 'All',
    });
    setPage(0);
  };

  const handleSettingsChangePage = (event, newPage) => {
    setSettingsPage(newPage);
  };

  const handleSettingsChangeRowsPerPage = (event) => {
    setSettingsRowsPerPage(parseInt(event.target.value, 10));
    setSettingsPage(0);
  };

  const handleOpenSettingsDialog = async (mode, settings = null) => {
    setSettingsDialogMode(mode);
    setEditingSettings(settings);
    setOpenSettingsDialog(true);

    if (mode !== 'add' && settings) {
      setSettingsFormData(getLeaveSettingsFormDataFromRecord(settings));
    } else {
      setSettingsFormData(getInitialLeaveSettingsFormData());
    }
    setSettingsFormErrors({});
  };

  const handleCloseSettingsDialog = () => {
    setOpenSettingsDialog(false);
    setEditingSettings(null);
    setSettingsFormData(getInitialLeaveSettingsFormData());
    setSettingsFormErrors({});
  };

  const handleSettingsInputChange = (field, value) => {
    setSettingsFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (settingsFormErrors[field]) {
      setSettingsFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateSettingsForm = async () => {
    try {
      await leaveSettingsValidationSchema.validate(settingsFormData, { abortEarly: false });
      setSettingsFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      setSettingsFormErrors(errors);
      return false;
    }
  };

  const handleSettingsSubmit = async () => {
    const isValid = await validateSettingsForm();
    if (!isValid) {
      toast.error('Please fix the form errors and try again.');
      return;
    }

    const submitData = formatLeaveSettingsDataForSubmission(settingsFormData);
    const success =
      settingsDialogMode === 'edit'
        ? await updateLeaveSettingsRecord(editingSettings.id, submitData)
        : await createLeaveSettingsRecord(submitData);

    if (success) {
      handleCloseSettingsDialog();
      handleLoadLeaveSettings();
    }
  };

  const handleDeleteSettings = (settings) => {
    setSettingsToDelete(settings);
    setOpenSettingsDeleteDialog(true);
  };

  const handleConfirmSettingsDelete = async () => {
    const success = await deleteLeaveSettingsRecord(settingsToDelete.id);
    if (success) {
      setOpenSettingsDeleteDialog(false);
      setSettingsToDelete(null);
      handleLoadLeaveSettings();
    }
  };

  const handleCancelSettingsDelete = () => {
    setOpenSettingsDeleteDialog(false);
    setSettingsToDelete(null);
  };

  const handleSettingsSearchChange = (event) => {
    setSettingsSearchTerm(event.target.value);
    setSettingsPage(0);
  };

  const handleSettingsFilterChange = (value) => {
    setSettingsEmployeeFilter(value);
    setSettingsPage(0);
  };

  const handleClearSettingsFilters = () => {
    setSettingsSearchTerm('');
    setSettingsEmployeeFilter('All');
    setSettingsPage(0);
  };

  return (
    <>
      <Helmet>
        <title>Leave Management | BillerBay</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Leave management"
            description="Review and approve leave requests, or switch to Leave settings to configure entitlements and policies per employee."
            actions={
              activeTab === 'requests' ? (
                <LightTooltip title="Submit a new leave request on behalf of an employee.">
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                    Add leave request
                  </Button>
                </LightTooltip>
              ) : (
                <LightTooltip title="Define leave balances or rules for an employee.">
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenSettingsDialog('add')}>
                    Add leave settings
                  </Button>
                </LightTooltip>
              )
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={(event, newValue) => setActiveTab(newValue)}
                aria-label="leave management tabs"
              >
                <Tab label="Leave Requests" value="requests" icon={<AssignmentIcon />} iconPosition="start" />
                <Tab label="Leaves Settings" value="settings" icon={<SettingsIcon />} iconPosition="start" />
              </Tabs>
            </Box>
          </Card>
        </Grid>

        {activeTab === 'requests' && (
          <>
            <LeaveStatsCards stats={stats} />
            <Grid item xs={12}>
              <LeaveFilters
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                employeeFilter={employeeFilter}
                leaveTypeFilter={leaveTypeFilter}
                dateRange={dateRange}
                filterOptions={filterOptions}
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
                onDateRangeChange={handleDateRangeChange}
                onClearFilters={handleClearFilters}
              />
            </Grid>

            <LeaveTable
              leaves={leaves}
              isLoading={isLoading}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onView={(leave) => handleOpenDialog('view', leave)}
              onEdit={(leave) => handleOpenDialog('edit', leave)}
              onDelete={handleDeleteLeave}
            />
          </>
        )}

        {activeTab === 'settings' && (
          <>
            <LeaveSettingsFilters
              searchTerm={settingsSearchTerm}
              employeeFilter={settingsEmployeeFilter}
              allEmployees={allEmployees}
              onSearchChange={handleSettingsSearchChange}
              onFilterChange={handleSettingsFilterChange}
              onClearFilters={handleClearSettingsFilters}
            />

            <LeaveSettingsTable
              leaveSettings={leaveSettings}
              isLoading={isSettingsLoading}
              page={settingsPage}
              rowsPerPage={settingsRowsPerPage}
              totalCount={settingsTotalCount}
              onPageChange={handleSettingsChangePage}
              onRowsPerPageChange={handleSettingsChangeRowsPerPage}
              onView={(setting) => handleOpenSettingsDialog('view', setting)}
              onEdit={(setting) => handleOpenSettingsDialog('edit', setting)}
              onDelete={handleDeleteSettings}
            />
          </>
        )}
      </Grid>

      <LeaveForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        initialEmployee={editingLeave?.employee}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <LeaveSettingsForm
        open={openSettingsDialog}
        mode={settingsDialogMode}
        formData={settingsFormData}
        formErrors={settingsFormErrors}
        allEmployees={allEmployees}
        onClose={handleCloseSettingsDialog}
        onSubmit={handleSettingsSubmit}
        onInputChange={handleSettingsInputChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the leave request for"
        itemName={leaveToDelete?.employee?.full_name}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete Leave Request"
      />

      <DeleteConfirmationDialog
        open={openSettingsDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the leave settings for"
        itemName={settingsToDelete?.employee?.full_name}
        onClose={handleCancelSettingsDelete}
        onConfirm={handleConfirmSettingsDelete}
        confirmButtonText="Delete Leave Settings"
      />
    </>
  );
}
